import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { WizardShell, type WizardStepDef } from '@/components/wizard';
import {
  useJackpotStore,
  useCurrentStep,
  useJackpotSaving,
  type WizardStep,
} from '@/state_management_and_stores/jackpot.store';
import { useToggleJackpotPublish, jackpotOpsKeys } from '@/api/extensions/useJackpotOps';
import { useToast } from '@/contexts/ToastContext';
import { BasicInfoStep, EventsStep, ReviewStep } from './JackpotWizardSteps';

/**
 * Ordered step labels shown in the WizardShell left nav.
 * Index 0 = step 1 (Basic Info). Order matches WizardStep values in the store.
 */
const WIZARD_STEPS: WizardStepDef[] = [
  { label: 'Basic Info' },
  { label: 'Events' },
  { label: 'Review' },
];

interface JackpotWizardProps {
  jackpotId?: string | null;
  onClose: () => void;
}

/**
 * JackpotWizard
 *
 * Orchestrates create/edit for a jackpot draft. Owns:
 *  - Zustand store wiring (load/reset/save/publish)
 *  - React Query invalidation
 *  - Toast notifications
 *  - Loading skeleton
 *
 * Delegates all wizard chrome (header with title/X, left nav, expand-all
 * toggle, footer) to the shared <WizardShell /> component. Visual layout
 * mirrors the "Add Event" modal: X in top-right, Previous/Next bottom-left,
 * Save Draft + Cancel + Save & Publish bottom-right, full rounded border.
 */
export function JackpotWizard({ jackpotId, onClose }: JackpotWizardProps) {
  const currentStep = useCurrentStep();
  const { isSaving, saveError } = useJackpotSaving();
  const isLoading = useJackpotStore((s) => s.isLoading);
  const basicInfo = useJackpotStore((s) => s.basicInfo);
  const { setStep, nextStep, prevStep, saveDraft, loadDraft, resetWizard } = useJackpotStore();
  const togglePublish = useToggleJackpotPublish();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    if (jackpotId) {
      loadDraft(jackpotId);
    } else {
      resetWizard();
    }
    return () => resetWizard();
  }, [jackpotId, loadDraft, resetWizard]);

  /**
   * 1-based step indices that are missing required data. Used to annotate the
   * left nav and disable Save Draft / Save & Publish. Step clicks and
   * Next/Previous buttons are always enabled — users can move around freely.
   */
  const missingSteps = new Set<number>();
  if (!basicInfo.name.trim() || !basicInfo.organization_id || !basicInfo.start_date) {
    missingSteps.add(1);
  }
  const hasMissing = missingSteps.size > 0;

  const handleSaveDraft = async () => {
    await saveDraft();
    const { saveError: err } = useJackpotStore.getState();
    if (!err) {
      queryClient.invalidateQueries({ queryKey: jackpotOpsKeys.lists() });
      showSuccess('Draft saved');
      onClose();
    } else {
      showError(err);
    }
  };

  const handlePublish = async () => {
    await saveDraft();
    const { saveError: err } = useJackpotStore.getState();
    if (err) {
      showError(err);
      return;
    }
    const id = useJackpotStore.getState().draftId || jackpotId;
    if (id) {
      try {
        await togglePublish.mutateAsync(id);
        showSuccess('Jackpot published');
      } catch (e: any) {
        showError(e.message || 'Failed to publish');
        return;
      }
    }
    queryClient.invalidateQueries({ queryKey: jackpotOpsKeys.lists() });
    onClose();
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6 space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    );
  }

  const secondaryAction = (
    <Button
      variant="outline"
      onClick={handleSaveDraft}
      disabled={isSaving || hasMissing}
      title={hasMissing ? 'Resolve the missing fields before saving.' : undefined}
    >
      {isSaving ? 'Saving...' : 'Save Draft'}
    </Button>
  );

  const primaryAction =
    currentStep < 3 ? null : (
      <Button
        onClick={handlePublish}
        disabled={isSaving || togglePublish.isPending || hasMissing}
        title={hasMissing ? 'Resolve the missing fields before publishing.' : undefined}
      >
        {isSaving || togglePublish.isPending ? 'Publishing...' : 'Save & Publish'}
      </Button>
    );

  return (
    <WizardShell
      title={jackpotId ? 'Edit Jackpot' : 'Create Jackpot'}
      onClose={onClose}
      steps={WIZARD_STEPS}
      currentStep={currentStep}
      onStepChange={(step) => setStep(step as WizardStep)}
      onNext={nextStep}
      onBack={prevStep}
      invalidSteps={missingSteps}
      // Next/Previous never block on validation — users may click any step.
      // Save buttons (secondaryAction/primaryAction) handle gating.
      secondaryAction={secondaryAction}
      primaryAction={primaryAction}
      belowContent={
        saveError ? (
          <p className="text-sm text-destructive">{saveError}</p>
        ) : null
      }
      allStepContent={[
        <BasicInfoStep key="step-1-basic-info" />,
        <EventsStep key="step-2-events" />,
        <ReviewStep key="step-3-review" />,
      ]}
      className="min-h-[85vh]"
    >
      {currentStep === 1 && <BasicInfoStep />}
      {currentStep === 2 && <EventsStep />}
      {currentStep === 3 && <ReviewStep />}
    </WizardShell>
  );
}
